/**
 * SEO Score — função pura, sem dependência de Vue/DOM externo.
 * Avalia título, meta description, palavra-chave foco e HTML do artigo,
 * e devolve um score 0-100 + lista de checks com status/mensagem/dica.
 *
 * Uso:
 *   import { computeSeoScore, seoScoreColor } from '@/lib/seoScore'
 *   const { score, checks } = computeSeoScore({ html, title, metaDescription, focusKeyword })
 */

const STATUS_WEIGHT = { good: 1, ok: 0.8, bad: 0 }

const PT_STOP_WORDS = new Set([
    'a', 'o', 'as', 'os', 'um', 'uma', 'uns', 'umas',
    'de', 'do', 'da', 'dos', 'das',
    'em', 'no', 'na', 'nos', 'nas',
    'por', 'pelo', 'pela', 'pelos', 'pelas',
    'para', 'com', 'sem', 'sob', 'sobre',
    'e', 'ou', 'mas', 'que', 'se',
    'ao', 'aos',
    'sao',
])

function normalize(text) {
    if (!text || typeof text !== 'string') return ''
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
}

function escapeRegex(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function countOccurrences(haystack, needle) {
    if (!haystack || !needle) return 0
    const pattern = new RegExp(`\\b${escapeRegex(needle)}\\b`, 'gi')
    const matches = haystack.match(pattern)
    return matches ? matches.length : 0
}

function contentTokens(normalizedText) {
    const raw = normalizedText.match(/\w+/g) || []
    return raw.filter(t => !PT_STOP_WORDS.has(t))
}

function keywordMatchLevel(normalizedTarget, normalizedKeyword) {
    if (!normalizedTarget || !normalizedKeyword) return 'none'
    if (normalizedTarget.includes(normalizedKeyword)) return 'exact'

    const kwContent = contentTokens(normalizedKeyword)
    if (kwContent.length === 0) return 'none'

    const targetSet = new Set(contentTokens(normalizedTarget))
    const hits = kwContent.filter(t => targetSet.has(t)).length
    return (hits / kwContent.length) >= 0.75 ? 'similar' : 'none'
}

function parseHtml(html) {
    if (!html || typeof html !== 'string') {
        return { doc: null, plainText: '', headings: { h1: [], h2: [], h3: [] }, firstParagraph: '' }
    }

    let doc = null
    try {
        doc = new DOMParser().parseFromString(`<div id="__seo_root">${html}</div>`, 'text/html')
    } catch (_) {
        doc = null
    }

    if (!doc) {
        const plain = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
        return { doc: null, plainText: plain, headings: { h1: [], h2: [], h3: [] }, firstParagraph: '' }
    }

    const root = doc.getElementById('__seo_root') || doc.body

    const collectText = (selector) => {
        const nodes = Array.from(root.querySelectorAll(selector))
        return nodes.map(n => (n.textContent || '').trim()).filter(Boolean)
    }

    const firstP = root.querySelector('p')
    const firstParagraph = firstP ? (firstP.textContent || '').trim() : ''

    const plainText = (root.textContent || '').replace(/\s+/g, ' ').trim()

    return {
        doc,
        plainText,
        headings: {
            h1: collectText('h1'),
            h2: collectText('h2'),
            h3: collectText('h3'),
        },
        firstParagraph,
    }
}

function buildCheck(id, label, status, message, tip, weight = 1, current = null) {
    return { id, label, status, message, tip, weight, current }
}

export function computeSeoScore({ html = '', title = '', metaDescription = '', focusKeyword = '' } = {}) {
    const checks = []

    const safeTitle = (title || '').toString().trim()
    const safeMeta = (metaDescription || '').toString().trim()
    const safeKw = (focusKeyword || '').toString().trim()
    const normKw = normalize(safeKw)

    const { plainText, headings, firstParagraph } = parseHtml(html)
    const normTitle = normalize(safeTitle)
    const normMeta = normalize(safeMeta)
    const normPlain = normalize(plainText)
    const normFirstP = normalize(firstParagraph)

    const wordCount = normPlain ? normPlain.split(/\s+/).filter(Boolean).length : 0

    // 1. Focus keyword set (gate)
    const hasFocus = normKw.length > 0
    checks.push(buildCheck(
        'focus_keyword_set',
        'Palavra-chave principal definida',
        hasFocus ? 'good' : 'bad',
        hasFocus
            ? `Foco definido: "${safeKw}"`
            : 'Defina a palavra-chave principal para que o sistema avalie a otimização do artigo.',
        'A palavra-chave principal é o termo que você quer ranquear no Google. Sem ela, vários checks não fazem sentido. Use 2-4 palavras (ex: "tratamento de varizes").',
        1,
    ))

    // 2. Title length (50-60 ideal, 40-65 ok)
    const tLen = safeTitle.length
    let titleStatus = 'bad'
    let titleMsg = ''
    if (tLen === 0) {
        titleMsg = 'O título está vazio.'
    } else if (tLen >= 50 && tLen <= 60) {
        titleStatus = 'good'
        titleMsg = `Tamanho ideal (${tLen} caracteres).`
    } else if (tLen >= 40 && tLen <= 65) {
        titleStatus = 'ok'
        titleMsg = `${tLen} caracteres — aceitável, mas ajuste para 50-60.`
    } else if (tLen < 40) {
        titleMsg = `Apenas ${tLen} caracteres — muito curto.`
    } else {
        titleMsg = `${tLen} caracteres — longo demais, será cortado na busca.`
    }
    checks.push(buildCheck(
        'title_length',
        'Título tem entre 50 e 60 caracteres',
        titleStatus,
        titleMsg,
        'Títulos entre 50 e 60 caracteres aparecem inteiros nos resultados do Google e têm a melhor taxa de cliques.',
        2,
        tLen,
    ))

    // 3. Title contains focus keyword
    if (hasFocus) {
        const level = keywordMatchLevel(normTitle, normKw)
        const titleHasKw = level !== 'none'
        const titleKwMsg =
            level === 'exact' ? 'A palavra-chave principal está no título.'
                : level === 'similar' ? 'A palavra-chave aparece no título em forma aproximada (variação aceita).'
                    : 'A palavra-chave principal não está no título.'
        checks.push(buildCheck(
            'title_has_keyword',
            'Palavra-chave aparece no título',
            titleHasKw ? 'good' : 'bad',
            titleKwMsg,
            'Incluir a palavra-chave no título é o sinal de relevância mais forte para o Google. De preferência, posicione-a no começo.',
            2,
        ))
    } else {
        checks.push(buildCheck('title_has_keyword', 'Palavra-chave aparece no título', 'na', 'Defina a palavra-chave principal acima.', '', 2))
    }

    // 4. Meta description length (140-160 ideal, 120-170 ok)
    const mLen = safeMeta.length
    let metaStatus = 'bad'
    let metaMsg = ''
    if (mLen === 0) {
        metaMsg = 'A meta description está vazia.'
    } else if (mLen >= 140 && mLen <= 160) {
        metaStatus = 'good'
        metaMsg = `Tamanho ideal (${mLen} caracteres).`
    } else if (mLen >= 120 && mLen <= 170) {
        metaStatus = 'ok'
        metaMsg = `${mLen} caracteres — quase ideal, ajuste para 140-160.`
    } else if (mLen < 120) {
        metaMsg = `Apenas ${mLen} caracteres — adicione mais informação.`
    } else {
        metaMsg = `${mLen} caracteres — longa demais, será cortada na SERP.`
    }
    checks.push(buildCheck(
        'meta_length',
        'Meta description tem entre 140 e 160 caracteres',
        metaStatus,
        metaMsg,
        'A meta description aparece logo abaixo do título no Google. Entre 140 e 160 caracteres garante que apareça inteira e convide ao clique.',
        2,
        mLen,
    ))

    // 5. Meta contains focus keyword
    if (hasFocus) {
        const level = keywordMatchLevel(normMeta, normKw)
        const metaHasKw = level !== 'none'
        const metaKwMsg =
            level === 'exact' ? 'A palavra-chave principal está na meta description.'
                : level === 'similar' ? 'A palavra-chave aparece na meta description em forma aproximada (variação aceita).'
                    : 'A palavra-chave não aparece na meta description.'
        checks.push(buildCheck(
            'meta_has_keyword',
            'Palavra-chave aparece na meta description',
            metaHasKw ? 'good' : 'bad',
            metaKwMsg,
            'O Google destaca em negrito a palavra-chave da busca quando ela aparece na meta description, aumentando o CTR.',
            1,
        ))
    } else {
        checks.push(buildCheck('meta_has_keyword', 'Palavra-chave aparece na meta description', 'na', 'Defina a palavra-chave principal acima.', '', 1))
    }

    // 6. Exactly 1 H1
    const h1Count = headings.h1.length
    let h1Status = 'bad'
    let h1Msg = ''
    if (h1Count === 1) {
        h1Status = 'good'
        h1Msg = 'O artigo tem exatamente 1 H1.'
    } else if (h1Count === 0) {
        h1Msg = 'Nenhum H1 encontrado no conteúdo.'
    } else {
        h1Msg = `${h1Count} H1s encontrados — deve haver apenas 1.`
    }
    checks.push(buildCheck(
        'h1_unique',
        'Exatamente 1 H1 no artigo',
        h1Status,
        h1Msg,
        'O H1 é o título principal do conteúdo. Múltiplos H1s confundem o Google sobre qual é o tópico principal. Use H2/H3 para subtítulos.',
        1,
        h1Count,
    ))

    // 7. At least 2 H2s
    const h2Count = headings.h2.length
    let h2Status = 'bad'
    let h2Msg = ''
    if (h2Count >= 4) {
        h2Status = 'good'
        h2Msg = `${h2Count} subtítulos H2 — boa estrutura.`
    } else if (h2Count >= 2) {
        h2Status = 'ok'
        h2Msg = `${h2Count} subtítulos H2 — considere adicionar mais.`
    } else if (h2Count === 1) {
        h2Msg = 'Apenas 1 subtítulo H2 — adicione mais para estruturar o artigo.'
    } else {
        h2Msg = 'Nenhum H2 encontrado — o artigo está sem estrutura clara.'
    }
    checks.push(buildCheck(
        'h2_count',
        'Pelo menos 2 subtítulos H2',
        h2Status,
        h2Msg,
        'Subtítulos H2 ajudam o leitor a escanear o artigo e o Google a entender a estrutura do conteúdo. Mire em 4-6 H2s para um artigo de 1200 palavras.',
        1,
        h2Count,
    ))

    // 8. Word count
    let wcStatus = 'bad'
    let wcMsg = ''
    if (wordCount >= 1200) {
        wcStatus = 'good'
        wcMsg = `${wordCount} palavras — ótimo para SEO.`
    } else if (wordCount >= 800) {
        wcStatus = 'ok'
        wcMsg = `${wordCount} palavras — aceitável, ideal seria 1200+.`
    } else if (wordCount >= 400) {
        wcMsg = `Apenas ${wordCount} palavras — conteúdo raso para o Google.`
    } else {
        wcMsg = `Apenas ${wordCount} palavras — muito pouco para ranquear.`
    }
    checks.push(buildCheck(
        'word_count',
        'Conteúdo com 800+ palavras (ideal 1200+)',
        wcStatus,
        wcMsg,
        'Artigos com mais palavras tendem a cobrir o tópico em profundidade e ranqueiam melhor. Não escreva por escrever — adicione informação útil.',
        2,
        wordCount,
    ))

    // 9. Keyword density (0.5% - 2.5%)
    if (hasFocus && wordCount > 0) {
        const occurrences = countOccurrences(normPlain, normKw)
        const density = (occurrences / wordCount) * 100
        const densityRounded = Math.round(density * 10) / 10
        let dStatus = 'bad'
        let dMsg = ''
        if (density >= 0.5 && density <= 2.5) {
            dStatus = 'good'
            dMsg = `${densityRounded}% (${occurrences} ocorrências) — densidade ideal.`
        } else if (density >= 0.3 && density < 0.5) {
            dStatus = 'ok'
            dMsg = `${densityRounded}% (${occurrences} ocorrências) — pouco abaixo do ideal.`
        } else if (density > 2.5 && density <= 3.5) {
            dStatus = 'ok'
            dMsg = `${densityRounded}% (${occurrences} ocorrências) — pouco acima, cuidado com keyword stuffing.`
        } else if (density < 0.3) {
            dMsg = `${densityRounded}% (${occurrences} ocorrências) — densidade baixa demais.`
        } else {
            dMsg = `${densityRounded}% (${occurrences} ocorrências) — densidade alta demais (keyword stuffing).`
        }
        checks.push(buildCheck(
            'keyword_density',
            'Densidade da palavra-chave entre 0,5% e 2,5%',
            dStatus,
            dMsg,
            'Use a palavra-chave naturalmente ao longo do texto. Densidade muito baixa enfraquece o sinal; muito alta soa artificial e pode ser penalizada.',
            2,
            densityRounded,
        ))
    } else {
        checks.push(buildCheck('keyword_density', 'Densidade da palavra-chave entre 0,5% e 2,5%', 'na', hasFocus ? 'Sem conteúdo para medir.' : 'Defina a palavra-chave principal acima.', '', 2))
    }

    // 10. Keyword in first paragraph
    if (hasFocus) {
        if (!normFirstP) {
            checks.push(buildCheck(
                'keyword_in_first_paragraph',
                'Palavra-chave no primeiro parágrafo',
                'bad',
                'Nenhum parágrafo encontrado no conteúdo.',
                'O primeiro parágrafo deve conter a palavra-chave principal — é onde o Google e o leitor confirmam o tópico.',
                1,
            ))
        } else {
            const level = keywordMatchLevel(normFirstP, normKw)
            const inFirst = level !== 'none'
            const firstMsg =
                level === 'exact' ? 'A palavra-chave aparece no primeiro parágrafo.'
                    : level === 'similar' ? 'A palavra-chave aparece no primeiro parágrafo em forma aproximada (variação aceita).'
                        : 'A palavra-chave não aparece no primeiro parágrafo.'
            checks.push(buildCheck(
                'keyword_in_first_paragraph',
                'Palavra-chave no primeiro parágrafo',
                inFirst ? 'good' : 'bad',
                firstMsg,
                'Mencionar a palavra-chave nas primeiras 100 palavras reforça o tópico para o Google e confirma para o leitor que ele está no lugar certo.',
                1,
            ))
        }
    } else {
        checks.push(buildCheck('keyword_in_first_paragraph', 'Palavra-chave no primeiro parágrafo', 'na', 'Defina a palavra-chave principal acima.', '', 1))
    }

    // 11. Keyword in subheadings (H2/H3)
    if (hasFocus) {
        const subheadings = [...headings.h2, ...headings.h3].map(normalize)
        const levels = subheadings.map(s => keywordMatchLevel(s, normKw))
        const hasExact = levels.includes('exact')
        const hasSimilar = levels.includes('similar')
        const found = hasExact || hasSimilar
        const subMsg = hasExact
            ? 'A palavra-chave aparece em pelo menos um subtítulo.'
            : hasSimilar
                ? 'A palavra-chave aparece em pelo menos um subtítulo em forma aproximada (variação aceita).'
                : 'A palavra-chave não aparece em nenhum subtítulo.'
        checks.push(buildCheck(
            'keyword_in_subheading',
            'Palavra-chave em pelo menos 1 H2 ou H3',
            found ? 'good' : 'bad',
            subMsg,
            'Incluir a palavra-chave em H2 ou H3 cria mais "pontos de aterrissagem" semânticos e ajuda o Google a entender a relevância de cada seção.',
            1,
        ))
    } else {
        checks.push(buildCheck('keyword_in_subheading', 'Palavra-chave em pelo menos 1 H2 ou H3', 'na', 'Defina a palavra-chave principal acima.', '', 1))
    }

    // Score: ignora checks 'na'
    let totalWeight = 0
    let earned = 0
    for (const c of checks) {
        if (c.status === 'na') continue
        totalWeight += c.weight
        earned += c.weight * (STATUS_WEIGHT[c.status] ?? 0)
    }
    const score = totalWeight > 0 ? Math.round((earned / totalWeight) * 100) : 0

    return { score, checks }
}

export function seoScoreColor(score) {
    if (score === null || score === undefined || isNaN(score)) return 'mercury'
    if (score >= 80) return 'success'
    if (score >= 50) return 'warning'
    return 'error'
}

export function seoScoreLabel(score) {
    if (score === null || score === undefined || isNaN(score)) return '—'
    if (score >= 80) return 'Bom'
    if (score >= 50) return 'Pode melhorar'
    return 'Precisa de atenção'
}
